import classNames from 'classnames'
import React, { useState } from 'react'
import { useBreakpoint } from '../../hooks/useBreakpoint'
import { useTranslations } from '../../hooks/useTranslations'
import { Breakpoints } from '../../utilities/breakpoints'
import { Translations } from '../../utilities/localization'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'
import { LinkProps } from '../Link'
import { InteractionType, LinkWrapper } from '../LinkWrapper'

export interface ImageGridItem {
  image: ImageProps
  link: LinkProps
}

export interface ImageGridProps {
  heading?: string
  items: ImageGridItem[]
  subHeading?: string
}

const renderItem = (
  item: ImageGridItem,
  position: number,
  translations: Translations,
  aboveTablet: boolean,
  lastRow: boolean
) => {
  if (position % 2 === 0) {
    return leftItem(item, position, translations, aboveTablet, lastRow)
  }

  return rightItem(item, position, translations, aboveTablet, lastRow)
}

const leftItem = (
  item: ImageGridItem,
  key: number,
  translations: Translations,
  aboveTablet: boolean,
  lastRow: boolean
) => {
  const content = (
    <>
      <aside
        className={classNames(
          'tb:w-2/3 tb:text-right tb:place-self-start-end tb:mt-30',
          'sm:w-auto sm:mr-60 sm:max-w-150 sm:flex-grow'
        )}
      >
        <LinkWrapper
          {...item.link}
          additionalLinkClassName="group"
          interactionType={InteractionType.None}
        >
          <span
            className={classNames(
              'block font-t text-t-400 text-primary',
              'sm:text-t-500',
              'xxl:text-t-600'
            )}
          >
            {item?.link?.title}
          </span>

          {!!item.link?.url && (
            <span
              className={classNames(
                'inline-block mt-15 pb-5 font-t text-t-200 border-b border-quinary transition-hover',
                'group-hover:opacity-hover group-hover:border-opacity-50 group-hover:border-secondary-darker',
                'xl:text-t-300'
              )}
            >
              {translations.more}
            </span>
          )}
        </LinkWrapper>
      </aside>

      <Image
        {...item.image}
        className="mt-30 tb:w-150 tb:h-90 sm:w-240 sm:h-150 sm:flex-shrink-0 xl:w-330 xl:h-240"
      />
    </>
  )

  return aboveTablet ? (
    <div
      className={classNames(
        'sm:col-start-1 sm:col-span-3 sm:flex sm:justify-end pt-30',
        'md:pt-60 md:mr-15',
        'lg:col-start-2 lg:col-span-3 lg:mr-45',
        'xl:col-start-2 xl:col-span-4 xl:mr-60 xl:pt-90',
        'xxl:col-start-4 xxl:col-span-4',
        {
          'pb-30 md:pb-60 xl:pb-90': !lastRow,
        }
      )}
      key={key}
    >
      {content}
    </div>
  ) : (
    <React.Fragment key={key}>{content}</React.Fragment>
  )
}

const rightItem = (
  item: ImageGridItem,
  key: number,
  translations: Translations,
  aboveTablet: boolean,
  lastRow: boolean
) => {
  const content = (
    <>
      <Image
        {...item.image}
        className="mt-30 tb:w-150 tb:h-90 sm:w-240 sm:h-150 sm:flex-shrink-0 xl:w-330 xl:h-240"
      />

      <aside className="tb:mt-30 sm:ml-60 sm:max-w-150 sm:flex-grow">
        <LinkWrapper
          {...item.link}
          additionalLinkClassName="group"
          interactionType={InteractionType.None}
        >
          <span
            className={classNames(
              'block font-t text-t-400 text-primary',
              'sm:text-t-500',
              'xxl:text-t-600'
            )}
          >
            {item?.link?.title}
          </span>

          {item?.link?.url && (
            <span
              className={classNames(
                'inline-block mt-15 pb-5 font-t text-t-200 border-b border-quinary transition-hover',
                'group-hover:opacity-hover group-hover:border-opacity-50 group-hover:border-secondary-darker',
                'xl:text-t-300'
              )}
            >
              {translations.more}
            </span>
          )}
        </LinkWrapper>
      </aside>
    </>
  )

  return aboveTablet ? (
    <div
      key={key}
      className={classNames(
        'sm:col-start-4 sm:col-span-3 sm:flex sm:justify-start pt-30',
        'md:pt-60 md:ml-15',
        'lg:col-start-5 lg:col-span-3 lg:ml-45',
        'xl:col-start-6 xl:col-span-4 xl:mr-60 xl:pt-90',
        'xxl:col-start-8 xxl:col-span-4',
        {
          'pb-30 md:pb-60 xl:pb-90': !lastRow,
        }
      )}
    >
      {content}
    </div>
  ) : (
    <React.Fragment key={key}>{content}</React.Fragment>
  )
}

export const ImageGrid = ({ heading, items, subHeading }: ImageGridProps) => {
  const translations = useTranslations()
  const [aboveMobile, setAboveMobile] = useState<boolean>(false)
  const [aboveTablet, setAboveTablet] = useState<boolean>(false)

  useBreakpoint(Breakpoints.tb, setAboveMobile)
  useBreakpoint(Breakpoints.sm, setAboveTablet)

  return (
    <Grid>
      {heading && (
        <h2 className="col-span-full font-h2 text-h2 text-center text-primary">
          {heading}
        </h2>
      )}

      {!isNullOrWhiteSpace(subHeading) && (
        <h3
          className={classNames(
            'col-span-full mt-45 font-h3 text-h3 mx-auto text-center text-secondary-dark',
            'tb:max-w-330',
            'sm:px-0 sm:max-w-[31.875rem]',
            'xxl:max-w-[43.125rem]'
          )}
        >
          {subHeading}
        </h3>
      )}

      {items.map((item, index) =>
        aboveMobile ? (
          renderItem(item, index, translations, aboveTablet, index >= items.length - 3)
        ) : (
          <LinkWrapper
            {...item.link}
            additionalClassName={`${index > 1 ? 'mt-45' : 'mt-30'}`}
            interactionType={InteractionType.None}
            key={index}
          >
            <Image {...item.image} />

            <span className="block mt-15 font-t text-t-400 text-primary">
              {item?.link?.title}
            </span>

            {!!item.link?.url && (
              <span className="mt-15 pb-5 font-t text-t-200 border-b border-quinary transition-hover hover:opacity-hover hover:border-opacity-50 hover:border-secondary-darker">
                {translations.more}
              </span>
            )}
          </LinkWrapper>
        )
      )}
    </Grid>
  )
}
